import { DateTime } from "luxon";
import { classes } from "polytype";
import {
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base";
import assertObjectFound from "../../base/assertObjectFound.js";
import { language } from "../../base/i18n/setup.js";
import { config } from "../../config/config.js";
import { File } from "../../file";
import type { AsyncResourceVariant } from "../../react";
import { provideReact } from "../../react";
import { User } from "../../user";
import type { Project } from "../Project";
import type {
  ProjectMembershipData,
  ProjectMembershipListItemData,
  ProjectMembershipListQueryData,
  ProjectRole,
} from "./types.js";

export class ProjectMembership extends ReferenceModel {
  public static ofId(id: string) {
    return new ProjectMembership(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.projectMembership.find(id);
    if (data) {
      return new ProjectMembershipDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const projectMembership = await this.find(id);
    assertObjectFound(projectMembership, "projectMembership", id);
    return projectMembership;
  });

  public findDetailed = provideReact(
    () => ProjectMembership.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<
    () => Promise<ProjectMembershipDetailed | undefined>
  >;

  public getDetailed = provideReact(
    () => ProjectMembership.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<ProjectMembershipDetailed>>;

  public static query(
    project: Project,
    query: ProjectMembershipListQueryData = {},
  ) {
    return new ProjectMembershipListQuery(project, query);
  }

  public async remove() {
    await config.behaviors.projectMembership.remove(this.id);
  }

  public static findOwn = provideReact(async (project: Project) => {
    const data = await config.behaviors.projectMembership.findOwn(project.id);
    if (data) {
      return new ProjectMembershipDetailed(data);
    }
  });

  public static getOwn = provideReact(async (project: Project) => {
    const membership = await this.findOwn(project);
    assertObjectFound(membership, "projectMembership", project.id);
    return membership;
  });
}

class ProjectMembershipCommon extends classes(
  DataModel<ProjectMembershipData | ProjectMembershipListItemData>,
  ProjectMembership,
) {
  public readonly user: User;
  public readonly role: ProjectRole;
  public readonly translatedRole: string;
  public readonly inherited: boolean;
  public readonly expiresAt?: DateTime;
  public readonly fullName: string;
  public readonly avatar?: File;

  public constructor(
    data: ProjectMembershipData | ProjectMembershipListItemData,
  ) {
    super([data], [data.id]);
    this.user = User.ofId(data.userId);
    this.role = data.role;
    this.translatedRole = language.default.project.role[this.role];
    this.inherited = data.inherited;
    if (data.expiresAt) {
      this.expiresAt = DateTime.fromISO(data.expiresAt);
    }
    this.fullName = `${data.firstName} ${data.lastName}`;
    this.avatar = data.avatarRef ? File.ofId(data.avatarRef) : undefined;
  }

  public async updateExpirationDate(expiresAt: string | null) {
    await config.behaviors.projectMembership.update(this.id, {
      expiresAt: expiresAt as string | undefined,
      role: this.role,
    });
  }

  public async updateRole(role: ProjectRole) {
    await config.behaviors.projectMembership.update(this.id, {
      expiresAt: this.data.expiresAt,
      role,
    });
  }
}

export class ProjectMembershipDetailed extends classes(
  ProjectMembershipCommon,
  DataModel<ProjectMembershipData>,
) {
  public constructor(data: ProjectMembershipData) {
    super([data], [data]);
  }
}

export class ProjectMembershipListItem extends classes(
  ProjectMembershipCommon,
  DataModel<ProjectMembershipListItemData>,
) {
  public constructor(data: ProjectMembershipListItemData) {
    super([data], [data]);
  }
}

export class ProjectMembershipListQuery extends ListQueryModel<ProjectMembershipListQueryData> {
  public readonly project: Project;

  public constructor(
    project: Project,
    query: ProjectMembershipListQueryData = {},
  ) {
    super(query, { dependencies: [project.id] });
    this.project = project;
  }

  public refine(query: ProjectMembershipListQueryData) {
    return new ProjectMembershipListQuery(this.project, {
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items, totalCount } = await config.behaviors.projectMembership.list(
      this.project.id,
      this.query,
    );

    return new ProjectMembershipList(
      this.project,
      this.query,
      items.map((d) => new ProjectMembershipListItem(d)),
      totalCount,
    );
  }, [this.queryId, "projectMembership"]);

  public getTotalCount = provideReact(async () => {
    const { totalCount } = await this.refine({ limit: 1 }).execute();
    return totalCount;
  }, [this.queryId, "projectMembership"]);
}

export class ProjectMembershipList extends classes(
  ProjectMembershipListQuery,
  ListDataModel<ProjectMembershipListItem>,
) {
  public constructor(
    project: Project,
    query: ProjectMembershipListQueryData,
    memberships: ProjectMembershipListItem[],
    totalCount: number,
  ) {
    super([project, query], [memberships, totalCount]);
  }
}
